<script lang="ts">
	//Svelte
	import { createEventDispatcher } from 'svelte';
	import { location } from 'svelte-spa-router';
	//Services
	import { format } from 'svelte-i18n';
	//Stores
	import { home_data } from 'src/stores/account/user';
import { devices } from 'src/stores/lists/devices';
import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	//Helpers
	import { isEmailUnverified } from 'src/helpers/email_verification_helpers';
	import { hasCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	//Design System Components
	import { MainMenu, MenuItem, MenuItemList } from '@Shelly/shelly-design-system/index';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	//Helpers
	import {
		appendMissingMenuOrderIndices,
		mainMenuDefaultOrder,
		menuItemCameraMonitoring,
		menuItemsNewLayout,
		menuItemsNewLayoutMore,
		MORE_MENU_ICON,
		parseHomeDataMenuOrderJson,
		type MenuItemDef as MenuItemType,
	} from 'src/helpers/main_menu';

	const dispatch = createEventDispatcher<{
		'menu-item-click': { menuItem: MenuItemType };
	}>();

	export let activeMenuItem: MenuItemType;
	export let menuItems = [...menuItemsNewLayout, ...menuItemsNewLayoutMore];

	const getActiveIcon = (icon: string) => {
		const parts = String(icon).trim().split(/\s+/).filter(Boolean);
		const iconClass = parts.length >= 2 ? parts[1] : (parts[0] ?? '');
		return `fas ${iconClass}`;
	};

	const resolveMenuItemNotification = (menuItem: MenuItemType) => {
		if (menuItem.href === '/settings' && $isEmailUnverified) return 'danger';
		if (menuItem.href === '/assistant' && !$home_data?.aiMenuItemSeen) return 'success';
		return menuItem.notification;
	};

	const handlerClickMenuItem = (menuItem: MenuItemType) => {
		activeMenuItem = menuItem;
		dispatch('menu-item-click', { menuItem });
	};

	// Highlight the More sub-item by the longest href that prefixes the current
	// location. This keeps Subscription (/settings/premium) highlighted only on
	// the subscription tab, while other /settings tabs highlight Settings.
	const isMoreSubItemActive = (subItem: MenuItemType, loc: string) => {
		const matches = menuItemsNewLayoutMore.filter((it) => it.href && loc.startsWith(it.href));
		if (!matches.length) return false;
		const best = matches.reduce((a, b) => ((b.href?.length ?? 0) > (a.href?.length ?? 0) ? b : a));
		return best.id === subItem.id;
	};

	// More button (Subscription / Settings / Explore Shelly) lives at the end of
	// the rail and opens a dropdown — it is not part of the reorderable items.
	let windowInnerWidth: number;
	let isMoreBtnFocused = false;
	let showMoreDropdown = false;

	$: isTabletOrMobile = windowInnerWidth < SystemScreenBreakpoints.TABLET_MAX;

	$: moreBtnDropdownOptions = {
		positionHorizontally: !isTabletOrMobile,
		closeDropdownHoverOut: true,
	};

	// Surface the camera monitoring entry in the "More" dropdown only when the user owns camera
	// devices (mirrors the dashboard camera tab gating).
	$: isCameraUser = hasCameraDevices($devices);
	$: moreMenuItems = isCameraUser
		? [...menuItemsNewLayoutMore, menuItemCameraMonitoring]
		: menuItemsNewLayoutMore;

	$: isMoreBtnActive = menuItemsNewLayoutMore.some((item) => item.id === activeMenuItem?.id);

	// Settings only lives inside the More dropdown on the new layout, so surface
	// the unverified-email red dot on the More button instead of the rail.
	$: isSettingsInMore =
		menuItemsNewLayoutMore.some((item) => item.href === '/settings') &&
		!menuItemsMainOrdered.some((item) => item.href === '/settings');

	const handleClickMoreBtn = () => {
		//For Desktop prevent main menu from closing on hover out when clicked
		if (!isTabletOrMobile) {
			moreBtnDropdownOptions.closeDropdownHoverOut = false;
		}

		isMoreBtnFocused = !isMoreBtnFocused;
		showMoreDropdown = !showMoreDropdown;
	};

	const handleMouseEnterMoreBtn = () => {
		if (isTabletOrMobile) return;

		if (showMoreDropdown) {
			moreBtnDropdownOptions.closeDropdownHoverOut = false;
			return;
		}

		isMoreBtnFocused = true;
		showMoreDropdown = true;
	};

	const handleCloseMoreBtn = () => {
		//For Desktop reset to default state dropdown options when user closes it
		if (!isTabletOrMobile) {
			moreBtnDropdownOptions.closeDropdownHoverOut = true;
		}

		isMoreBtnFocused = false;
	};

	const handleClickMoreSubItem = (subMenuItem: MenuItemType) => {
		showMoreDropdown = false;
		isMoreBtnFocused = false;
		handlerClickMenuItem(subMenuItem);
	};

	$: menuOrder = appendMissingMenuOrderIndices(
		parseHomeDataMenuOrderJson($home_data?.menuOrder),
		menuItemsNewLayout,
	);

	$: availableMenuItemIds = new Set(menuItems.map((menuItem) => menuItem.id));
	$: menuItemsMainOrdered = menuOrder
		.map((position) => menuItemsNewLayout[position])
		.filter(
			(menuItem): menuItem is MenuItemType => !!menuItem && availableMenuItemIds.has(menuItem.id),
		);

	$: mainMenuDockPartition = (() => {
		const primary: MenuItemType[] = [];
		const footer: MenuItemType[] = [];
		for (const it of menuItemsMainOrdered) {
			if (it.mainMenuDock === 'footer') footer.push(it);
			else primary.push(it);
		}
		return { primary, footer };
	})();
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

<MainMenu useNewLayout>
	<div class="new-layout-main-menu-dock">
		<div class="new-layout-main-menu-dock__primary">
			{#each mainMenuDockPartition.primary as menuItem}
				{@const notification = resolveMenuItemNotification(menuItem)}
				{@const isActive = activeMenuItem?.id === menuItem.id}
				<MenuItem
					useNewLayout
					active={isActive}
					icon={isActive ? getActiveIcon(menuItem.icon) : menuItem.icon}
					label={$format(menuItem.label)}
					{notification}
					on:click={() => {
						handlerClickMenuItem(menuItem);
					}}
				/>
			{/each}

			<!-- More button: Subscription / Settings / Explore Shelly dropdown -->
			<MenuItem
				useNewLayout
				bind:showDropdown={showMoreDropdown}
				dropdownOptions={moreBtnDropdownOptions}
				active={isMoreBtnActive}
				isFocused={isMoreBtnFocused}
				icon={isMoreBtnActive ? getActiveIcon(MORE_MENU_ICON) : MORE_MENU_ICON}
				label={$format('title_more', { default: 'More' })}
				notification={isSettingsInMore && $isEmailUnverified ? 'danger' : undefined}
				on:click={handleClickMoreBtn}
				on:mouseenter={handleMouseEnterMoreBtn}
				on:close={handleCloseMoreBtn}
			>
				<svelte:fragment slot="dropdown-menu-items">
					<MenuItemList isGroupTitle title={$format('title_more', { default: 'More' })} />

					{#each moreMenuItems as subMenuItem}
						{@const isActive = isMoreSubItemActive(subMenuItem, $location ?? '')}
						{@const showUnverifiedEmailDot =
							subMenuItem.href === '/settings' && $isEmailUnverified}
						<div
							class="more-menu-item-wrapper"
							class:has-notification={showUnverifiedEmailDot}
						>
							<MenuItemList
								isGroupItem
								icon={subMenuItem.icon}
								title={$format(subMenuItem.label, {
									default: subMenuItem.labelDefault,
								})}
								active={isActive}
								on:click={() => {
									handleClickMoreSubItem(subMenuItem);
								}}
							/>
						</div>
					{/each}
				</svelte:fragment>
			</MenuItem>
		</div>
		{#if mainMenuDockPartition.footer.length}
			<div class="new-layout-main-menu-dock__grow" aria-hidden="true"></div>
			<div class="new-layout-main-menu-dock__footer">
				{#each mainMenuDockPartition.footer as menuItem}
					{@const notification = resolveMenuItemNotification(menuItem)}
					{@const isActive = activeMenuItem?.id === menuItem.id}
					<MenuItem
						useNewLayout
						active={isActive}
						icon={isActive ? getActiveIcon(menuItem.icon) : menuItem.icon}
						label={$format(menuItem.label)}
						{notification}
						on:click={() => {
							handlerClickMenuItem(menuItem);
						}}
					/>
				{/each}
			</div>
		{/if}
		{#if $$slots.footer}
			<div class="main-menu-footer">
				<slot name="footer" />
			</div>
		{/if}
	</div>
</MainMenu>

<style>
	.more-menu-item-wrapper {
		position: relative;
	}

	.more-menu-item-wrapper.has-notification::after {
		content: '';
		position: absolute;
		top: 50%;
		right: 12px;
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background-color: var(--color-danger);
		transform: translateY(-50%);
		pointer-events: none;
	}

	:global(.main-layout.new-layout .new-layout-main-menu-dock) {
		min-height: 0;
		min-width: 0;
		width: 100%;
		align-self: stretch;
	}

	/* Row gap/justify: same breakpoint block as .new-layout-main-menu-dock below */
	@media screen and (max-width: 1123px) {
		:global(.main-layout.new-layout .new-layout-main-menu-dock) {
			display: flex;
			flex: 1 1 auto;
			min-width: 0;
		}
	}

	.new-layout-main-menu-dock {
		display: flex;
		width: 100%;
		min-width: 0;
		align-items: stretch;
	}

	@media screen and (min-width: 1124px) {
		:global(.main-layout.new-layout .new-layout-main-menu-dock) {
			display: contents;
		}

		/* When :global selector does not match, keep vertical stack on the rail */
		.new-layout-main-menu-dock {
			flex-direction: column;
			flex: 1 1 auto;
			min-height: 0;
		}

		.new-layout-main-menu-dock__primary {
			display: flex;
			flex-direction: column;
			flex: 0 0 auto;
			width: 100%;
			align-self: stretch;
		}

		.new-layout-main-menu-dock__grow {
			flex: 1 1 auto;
			min-height: var(--spacing-xl);
			width: 100%;
		}

		.new-layout-main-menu-dock__footer {
			display: flex;
			flex-direction: column;
			flex: 0 0 auto;
			width: 100%;
			align-self: stretch;
			padding-top: var(--spacing-s);
			border-top: 1px solid var(--color-border, rgba(255, 255, 255, 0.12));
		}

		.main-menu-footer {
			width: 100%;
			align-self: stretch;
			flex: 0 0 auto;
		}
	}

	@media screen and (max-width: 1123px) {
		.new-layout-main-menu-dock {
			flex-direction: row;
			align-items: center;
			justify-content: center;
			gap: var(--spacing-s);
			min-width: 0;
		}

		.new-layout-main-menu-dock__primary {
			display: flex;
			flex-direction: row;
			flex: 0 1 auto;
			align-items: center;
			min-width: 0;
			gap: var(--spacing-s);
		}

		/* Spacer only for vertical desktop rail; omit on horizontal tablet so primary + footer stay one group */
		.new-layout-main-menu-dock__grow {
			display: none;
		}

		.new-layout-main-menu-dock__footer {
			display: flex;
			flex-direction: row;
			flex: 0 0 auto;
			align-items: center;
			min-width: 0;
			gap: var(--spacing-s);
		}

		.main-menu-footer {
			flex: 0 0 auto;
			min-width: 0;
		}

		:global(.main-layout.new-layout .new-layout-main-menu-dock .menu-item.new-layout) {
			margin-inline: 0;
		}
	}

	/* SystemScreenBreakpoints.MOBILE_MAX (639) — single flex row: equal cells, one gap rhythm */
	@media screen and (max-width: 639px) {
		.new-layout-main-menu-dock__primary,
		.new-layout-main-menu-dock__footer {
			display: contents;
		}

		:global(.main-layout.new-layout .new-layout-main-menu-dock .menu-item.new-layout) {
			box-sizing: border-box;
			flex: 1 1 0;
			min-width: 0;
			max-width: 70px;
			width: auto;
			padding: 7px 0 8px;
		}

		:global(.main-layout.new-layout) .main-menu-footer {
			box-sizing: border-box;
			flex: 1 1 0;
			min-width: 0;
			max-width: 70px;
			width: auto;
			padding: 7px 0 8px;
			justify-content: center;
			align-items: center;
		}
	}

	@media screen and (min-width: 640px) and (max-width: 1123px) {
		:global(.main-layout.new-layout .new-layout-main-menu-dock .menu-item.new-layout) {
			box-sizing: border-box;
			width: auto;
			padding: 7px 20px 8px 20px;
		}

		:global(.main-layout.new-layout) .main-menu-footer {
			box-sizing: border-box;
			width: auto;
			padding: 7px 20px 8px 20px;
		}
	}

	.main-menu-footer {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-top: auto;
		padding: var(--spacing-s) 0;
	}

	@media screen and (max-width: 1123px) {
		.main-menu-footer {
			margin-top: 0;
		}
	}
</style>
